import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ProtocolPhotoGallery } from "@/components/equipment/ProtocolPhotoGallery";
import { protocolDirectionLabel } from "@/components/equipment/equipmentLabels";
import type { EquipmentProtocol } from "@/types/equipment";

interface ProtocolReviewDialogProps {
  protocol: EquipmentProtocol | null;
  itemName: string;
  serialNumber: string | null;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  onReject: (reason: string) => void;
}

export function ProtocolReviewDialog({
  protocol,
  itemName,
  serialNumber,
  saving,
  onOpenChange,
  onAccept,
  onReject,
}: ProtocolReviewDialogProps) {
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");

  const resetAndClose = (open: boolean) => {
    if (!open) {
      setRejectMode(false);
      setReason("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={Boolean(protocol)} onOpenChange={resetAndClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{protocol ? protocolDirectionLabel(protocol.direction) : "Protokół"}</DialogTitle>
          <DialogDescription>
            Sprawdź dane przedmiotu. Możesz tylko zaakceptować albo odrzucić — bez zmiany opisu stanu i zdjęć.
          </DialogDescription>
        </DialogHeader>
        {protocol && (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-xs text-muted-foreground">Przedmiot</p>
              <p className="font-medium">{itemName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Numer seryjny</p>
              <p className="font-medium">{serialNumber || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Stan wpisany przez koordynatora</p>
              <p className="whitespace-pre-wrap text-sm">{protocol.condition_notes?.trim() || "Brak opisu stanu."}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Zdjęcia</p>
              <ProtocolPhotoGallery paths={protocol.photo_urls ?? []} />
            </div>
            {rejectMode && (
              <div className="space-y-2">
                <Label htmlFor="equipment-reject-reason">Powód odrzucenia</Label>
                <Textarea
                  id="equipment-reject-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Opisz, dlaczego nie przyjmujesz przedmiotu..."
                  maxLength={2000}
                />
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => {
              if (rejectMode) {
                setRejectMode(false);
                setReason("");
                return;
              }
              resetAndClose(false);
            }}
            disabled={saving}
          >
            {rejectMode ? "Wróć" : "Anuluj"}
          </Button>
          {rejectMode ? (
            <Button
              variant="destructive"
              disabled={saving || !reason.trim()}
              onClick={() => onReject(reason.trim())}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Potwierdź odrzucenie
            </Button>
          ) : (
            <>
              <Button variant="destructive" disabled={saving} onClick={() => setRejectMode(true)}>
                Odrzuć
              </Button>
              <Button disabled={saving} onClick={onAccept}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Akceptuj
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
